import type { SharedTranscriptStreamEvent } from '@/lib/domain-query/timeline';

import type { LiveRunState, TimelineStreamEvent } from './chat-transcript';

const CHAT_DEBUG_STORAGE_KEY = 'crewstack:chat-debug';
const CHAT_DEBUG_BUFFER_KEY = '__chatDebugLogs';
const CHAT_DEBUG_BUFFER_LIMIT = 1000;

const CHAT_DEBUG_PATH_MARKERS = ['/conversations', '/messages', '/timeline/stream', '/sandbox', '/sandboxes', '/runs'];

type ChatDebugEntry = {
	timestamp: string;
	scope: string;
	message: string;
	details?: unknown;
};

declare global {
	interface Window {
		__chatDebugLogs?: ChatDebugEntry[];
	}
}

export function isChatDebugEnabled() {
	if (typeof window === 'undefined') {
		return false;
	}

	const raw = window.localStorage.getItem(CHAT_DEBUG_STORAGE_KEY);
	return raw === '1' || raw === 'true' || raw === 'on';
}

export function isChatDebugPath(path: string) {
	return CHAT_DEBUG_PATH_MARKERS.some((marker) => path.includes(marker));
}

export function chatDebugLog(scope: string, message: string, details?: unknown) {
	if (!isChatDebugEnabled()) {
		return;
	}

	appendChatDebugEntry({
		timestamp: new Date().toISOString(),
		scope,
		message,
		...(details === undefined ? {} : { details: sanitizeForDebugBuffer(details) }),
	});

	const prefix = `[chat-debug][${scope}] ${message}`;
	if (details === undefined) {
		console.info(prefix);
		return;
	}

	console.info(prefix, details);
}

export function chatDebugEnabledDetails() {
	if (typeof window === 'undefined') {
		return null;
	}

	return {
		enabled: isChatDebugEnabled(),
		storageKey: CHAT_DEBUG_STORAGE_KEY,
		storageValue: window.localStorage.getItem(CHAT_DEBUG_STORAGE_KEY),
		href: window.location.href,
		bufferKey: CHAT_DEBUG_BUFFER_KEY,
		bufferSize: window[CHAT_DEBUG_BUFFER_KEY]?.length ?? 0,
	};
}

export function summarizeTimelineEvent(event: TimelineStreamEvent) {
	return {
		eventId: event.event_id,
		eventType: event.event_type,
		conversationId: readString(event.stream.conversation_id) ?? null,
		runId: readString(event.stream.run_id) ?? null,
		cursor: readString(event.stream.stream_cursor) ?? null,
		segmentKind: readString(event.payload.segment_kind) ?? null,
		messageId: readString(event.payload.message_id) ?? null,
		requestId: readString(event.payload.request_id) ?? null,
		toolCallId: readString(event.payload.tool_call_id) ?? null,
		status: readString(event.payload.status) ?? null,
	};
}

export function summarizeSharedTranscriptEvent(event: SharedTranscriptStreamEvent) {
	return {
		cursor: event.cursor,
		eventType: event.eventType,
		transcriptKind: event.transcriptKind,
		entryId: event.entry.id,
		runId: event.entry.relatedRunId,
		messageKind: event.entry.messageKind,
		status: event.entry.status,
	};
}

export function summarizeLiveRuns(runs: Record<string, LiveRunState>) {
	return Object.values(runs).map((run) => ({
		runId: run.runId,
		conversationId: run.conversationId,
		phase: run.phase,
		status: run.status,
		draftTextLength: run.draftText.length,
		finalTextLength: run.finalText.length,
		thinkingTextLength: run.thinkingText.length,
		segmentKinds: run.activitySegments.map((segment) => segment.type),
		commitSegmentCursor: run.commitSegmentCursor,
		toolCalls: run.toolCalls.map((toolCall) => ({
			id: toolCall.id,
			name: toolCall.name,
			status: toolCall.status,
			outputLength: toolCall.output.length,
		})),
		hasPendingApproval: run.pendingApprovalRequestId != null && run.pendingApprovalRequestId !== '',
		errorMessage: run.errorMessage,
	}));
}

function readString(value: unknown) {
	return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function appendChatDebugEntry(entry: ChatDebugEntry) {
	if (typeof window === 'undefined') {
		return;
	}

	const buffer = (window[CHAT_DEBUG_BUFFER_KEY] ??= []);
	buffer.push(entry);
	if (buffer.length > CHAT_DEBUG_BUFFER_LIMIT) {
		buffer.splice(0, buffer.length - CHAT_DEBUG_BUFFER_LIMIT);
	}
}

function sanitizeForDebugBuffer(value: unknown): unknown {
	if (value === undefined) {
		return undefined;
	}

	try {
		return JSON.parse(JSON.stringify(value)) as unknown;
	} catch {
		return String(value);
	}
}
